import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { useTranslation } from 'react-i18next';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useGetWorkflowsByAgency } from '@frontend/features/workflows/queries';

import { WEBHOOK_EVENTS, WebhookConfig, WebhookFormValues, useSaveWebhook } from '../queries';

interface Props {
  agencyId: number;
  webhook?: WebhookConfig;
  onClose: () => void;
}

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

export const WebhookForm = ({ agencyId, webhook, onClose }: Props) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();
  const mutation = useSaveWebhook(agencyId, webhook?.id);

  const { control, handleSubmit, reset } = useForm<WebhookFormValues>({
    defaultValues: {
      url: '',
      secret: '',
      events: [],
      workflowIds: [],
      isActive: true,
    },
  });

  const { data: workflowsData, isLoading: workflowsLoading, isError: workflowsError } = useGetWorkflowsByAgency({
    agencyId,
    pageSize: 1000,
    currentPage: 1,
    search: '',
  });

  const eventOptions = useMemo(
    () => WEBHOOK_EVENTS.map((value) => ({ id: value, name: t(`webhooks.events.${value}`) })),
    [t],
  );

  useEffect(() => {
    if (webhook) {
      reset({
        url: webhook.url,
        secret: '',
        events: webhook.events || [],
        workflowIds: webhook.workflowIds || [],
        isActive: webhook.isActive,
      });
    }
  }, [reset, webhook]);

  const onSubmit = async (values: WebhookFormValues) => {
    if (values.events.length === 0) {
      addToast({ message: t('webhooks.form.eventsRequired'), color: 'error' });
      return;
    }
    try {
      await mutation.mutateAsync(values);
      addToast({ message: t('webhooks.form.saveSuccess'), color: 'success' });
      onClose();
    } catch (e) {
      addToast({ message: t('webhooks.form.saveError'), color: 'error' });
    }
  };

  const workflowOptions = useMemo(
    () => (workflowsData?.items || []).map((wf) => ({ id: wf.id, name: wf.name })),
    [workflowsData],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box display="flex" flexDirection="column" gap={2} marginTop={1}>
        <Controller
          name="url"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField {...field} label={t('webhooks.form.url')} placeholder="https://..." fullWidth />
          )}
        />

        <Controller
          name="secret"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('webhooks.form.secret')}
              fullWidth
              helperText={webhook ? t('webhooks.form.secretEditHelper') : t('webhooks.form.secretHelper')}
            />
          )}
        />

        <Controller
          name="events"
          control={control}
          render={({ field }) => {
            const selectedIds: string[] = field.value || [];
            const selectedOptions = eventOptions.filter((opt) => selectedIds.includes(opt.id));
            return (
              <Box display="flex" flexDirection="column" gap={1}>
                <Autocomplete
                  multiple
                  options={eventOptions}
                  value={selectedOptions}
                  onChange={(_, newValue) => field.onChange(newValue.map((o) => o.id))}
                  getOptionLabel={(option) => option.name}
                  disableCloseOnSelect
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
                    ))
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.id}>
                      <Checkbox
                        icon={checkboxIcon}
                        checkedIcon={checkboxCheckedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('webhooks.form.events')}
                      placeholder={t('webhooks.form.eventsPlaceholder')}
                    />
                  )}
                />
              </Box>
            );
          }}
        />

        <Controller
          name="workflowIds"
          control={control}
          render={({ field }) => {
            const selectedIds: string[] = field.value || [];
            const selectedOptions = workflowOptions.filter((opt) => selectedIds.includes(opt.id));
            return (
              <Box display="flex" flexDirection="column" gap={1}>
                <Autocomplete
                  multiple
                  options={workflowOptions}
                  value={selectedOptions}
                  onChange={(_, newValue) => field.onChange(newValue.map((o) => o.id))}
                  getOptionLabel={(option) => option.name}
                  disableCloseOnSelect
                  disabled={workflowsLoading || workflowsError}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
                    ))
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.id}>
                      <Checkbox
                        icon={checkboxIcon}
                        checkedIcon={checkboxCheckedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('webhooks.form.workflows')}
                      placeholder={t('webhooks.form.workflowsPlaceholder')}
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('webhooks.form.workflowsHelper')}
                </Typography>
              </Box>
            );
          }}
        />

        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
              label={t('webhooks.form.active')}
            />
          )}
        />

        <Button variant="contained" type="submit" disabled={mutation.isLoading}>
          {webhook ? t('webhooks.form.update') : t('webhooks.form.create')}
        </Button>
      </Box>
    </form>
  );
};
